import styled, { css } from 'styled-components';
import { getBgColorByBgStyle } from '~/helpers/getColors';
import getFontFamily from '~/helpers/getFontFamily';
import { mq } from '~/styles/grid';
import { CopyStandard } from '../../atoms/Copytext';
import Headline from '../../atoms/Headline/Headline';
import { Headline500Skin } from '../../atoms/Headline/HeadlineSkins';
import Picture from '../../atoms/Picture';

export const FactContent = styled.div`
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 10;
`;

/**
 * @typedef {Object} FactHeaderProps
 * @property {boolean} $narrow
 */

export const FactHeader = styled.div`
  display: flex;
  flex-direction: ${(/** @type {FactHeaderProps} */ props) =>
    props.$narrow ? 'column' : 'row'};
  align-items: ${(/** @type {FactHeaderProps} */ props) =>
    props.$narrow ? 'flex-start' : 'flex-end'};
  flex-wrap: wrap;

  @media (${mq.xs}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const FactFigure = styled(Headline)`
  font-size: 48px;
  line-height: 1.2;
  font-family: ${(/** @type {{ theme: Theme }} */ props) =>
    getFontFamily(props.theme.fact?.figureFont)};
  color: ${(/** @type {{ $fontColor: string }} */ props) =>
    props.$fontColor} !important;
  margin-right: 15px;
  margin-bottom: 0;
  flex-shrink: 0;
  max-width: 100%;
  white-space: pre-wrap;
  @media (${mq.xs}) {
    font-size: 40px;
  }
`;

export const FactHeadline = styled(Headline)`
  ${Headline500Skin};
  color: ${(/** @type {{ $fontColor: string }} */ props) =>
    props.$fontColor} !important;
  margin-top: 0;
  margin-bottom: 0;
  font-size: 23px;
  line-height: 1.2;
  white-space: pre-wrap;
`;

export const FactText = styled(CopyStandard)`
  color: ${(/** @type {{ $fontColor: string }} */ props) =>
    props.$fontColor} !important;
  font-size: 18px;
  line-height: 1.5;
  white-space: pre-wrap;
  margin-top: 0;
`;

export const PictureStyled = styled(Picture)`
  position: absolute;
  overflow: hidden;
  top: 0;
  height: 100%;

  img {
    opacity: 0.5;
    object-fit: cover;
    object-position: center center;
    width: 100%;
    height: 100%;
    max-width: none;
  }
`;

export const FactsGrid = styled.div`
  display: grid;
  align-items: start;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }

  ${({ $count }) => {
    if ($count <= 1) {
      return css`
        grid-template-columns: 1fr;
      `;
    }

    if ($count === 2) {
      return css`
        @media (min-width: 992px) {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      `;
    }

    return css`
      @media (min-width: 992px) and (max-width: 1199px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: auto auto auto;
      }

      @media (min-width: 1200px) {
        grid-template-columns: 451px minmax(0, 1fr);
        grid-template-rows: auto auto auto;
      }
    `;
  }}
`;

export const FactsCard = styled.article`
  min-width: 0;

  background-color: ${(props) =>
    getBgColorByBgStyle(props.theme, props.$factStyle)};
  background-size: cover;

  @media (max-width: 991px) {
    grid-column: auto !important;
    grid-row: auto !important;
    height: auto !important;
    align-self: auto !important;
  }

  @media (max-width: 991px) {
    grid-column: auto !important;
    grid-row: auto !important;
    height: auto !important;
    align-self: auto !important;
  }

  ${({ $count, $index }) => {
    if ($count <= 1) {
      return css`
        @media (min-width: 992px) {
          grid-column: 1;
          grid-row: 1;
        }
      `;
    }

    if ($count === 2) {
      return css`
        @media (min-width: 992px) {
          grid-column: ${$index + 1};
          grid-row: 1;
          height: 100%;
        }
      `;
    }

    if ($count === 3) {
      return css`
        @media (min-width: 992px) and (max-width: 1199px) {
          ${$index === 0 &&
          css`
            grid-column: 1;
            grid-row: 1;
          `}
          ${$index === 1 &&
          css`
            grid-column: 1;
            grid-row: 2;
          `}
          ${$index === 2 &&
          css`
            grid-column: 2;
            grid-row: 1 / 3;
            align-self: stretch;
            height: 100%;
          `}
        }

        @media (min-width: 1200px) {
          ${$index === 0 &&
          css`
            grid-column: 1;
            grid-row: 1;
          `}
          ${$index === 1 &&
          css`
            grid-column: 1;
            grid-row: 2;
          `}
          ${$index === 2 &&
          css`
            grid-column: 2;
            grid-row: 1 / 3;
            align-self: stretch;
            height: 100%;
          `}
        }
      `;
    }

    if ($count >= 4) {
      return css`
        @media (min-width: 992px) and (max-width: 1199px) {
          ${$index === 0 &&
          css`
            grid-column: 1;
            grid-row: 1;
          `}
          ${$index === 1 &&
          css`
            grid-column: 1;
            grid-row: 2;
          `}
          ${$index === 2 &&
          css`
            grid-column: 2;
            grid-row: 1 / 3;
            align-self: stretch;
            height: 100%;
          `}
          ${$index === 3 &&
          css`
            grid-column: 1 / -1;
            grid-row: 3;
          `}
        }

        @media (min-width: 1200px) {
          ${$index === 0 &&
          css`
            grid-column: 1;
            grid-row: 1;
          `}
          ${$index === 1 &&
          css`
            grid-column: 1;
            grid-row: 2;
          `}
          ${$index === 2 &&
          css`
            grid-column: 2;
            grid-row: 1 / 3;
            align-self: stretch;
            height: 100%;
          `}
          ${$index === 3 &&
          css`
            grid-column: 1 / -1;
            grid-row: 3;
          `}
        }
      `;
    }
  }}
`;
